import { Graphics } from "pixi.js";
import type { GameConfig } from "../config/GameConfig";
import { rgbToHex } from "../config/Palette";
import { clamp } from "../core/math";
import type { WorldBounds } from "../core/WorldBounds";
import type { Divider } from "../entities/Divider";
import type { ForceField } from "../physics/ForceField";

interface Tracer {
  x: number;
  y: number;
  age: number;
  size: number;
  shade: number;
}

/**
 * Traçadores visuais do campo de forças. Só advectam com os vectores;
 * não entram na física nem nas bolhas.
 */
export class WindParticles {
  readonly view = new Graphics();
  private readonly tracers: Tracer[] = [];

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
  ) {
    this.view.eventMode = "none";
    this.syncCount(true);
  }

  resize(): void {
    this.syncCount(false);
  }

  update(dt: number, time: number, field: ForceField, divider: Divider): void {
    const cfg = this.config.particles;
    if (!cfg.enabled) {
      this.view.clear();
      this.view.visible = false;
      return;
    }
    this.view.visible = true;
    this.syncCount(false);

    const w = this.bounds.width;
    const h = this.bounds.height;
    const life = Math.max(0.2, cfg.life);
    const peak = clamp(cfg.peak, 0.05, life - 0.05);
    const fade = Math.max(0.05, life - peak);
    const scale = cfg.advection;
    const vis = this.config.visual;
    const g = this.view;
    g.clear();

    for (const tracer of this.tracers) {
      tracer.age += dt;
      if (tracer.age >= life) this.birth(tracer, false);

      const sample = field.sample(tracer.x, tracer.y, time, divider);
      tracer.x += sample.x * scale * dt;
      tracer.y += sample.y * scale * dt;

      if (tracer.x < 0) tracer.x += w;
      else if (tracer.x >= w) tracer.x -= w;
      if (tracer.y < 0) tracer.y += h;
      else if (tracer.y >= h) tracer.y -= h;

      const alpha = cfg.maxAlpha * envelope(tracer.age, peak, fade);
      if (alpha < 0.01) continue;

      const geoAbove = divider.isAbove(tracer.x, tracer.y);
      const useUpper = vis.swapEnvironments ? !geoAbove : geoAbove;
      const palette = useUpper ? vis.upper : vis.lower;
      g.circle(tracer.x, tracer.y, tracer.size);
      g.fill({ color: rgbToHex(palette.fill), alpha });
    }
  }

  /**
   * Estrelas 2px no ecrã inicial (mesma física):
   *   a
   *  aba
   *   a
   * a = cinza 50%, b = preto 100%.
   */
  paintMono(ctx: CanvasRenderingContext2D): void {
    const cfg = this.config.particles;
    const m = ctx.getTransform();
    const w = Math.max(1, Math.round(ctx.canvas.width / (m.a || 1)));
    const h = Math.max(1, Math.round(ctx.canvas.height / (m.d || 1)));
    ctx.clearRect(0, 0, w, h);
    if (!cfg.enabled) return;

    const life = Math.max(0.2, cfg.life);
    const peak = clamp(cfg.peak, 0.05, life - 0.05);
    const fade = Math.max(0.05, life - peak);

    const wrap = (n: number, max: number) => {
      const v = n % max;
      return v < 0 ? v + max : v;
    };
    const dot = (x: number, y: number) => {
      ctx.fillRect(wrap(x, w), wrap(y, h), 1, 1);
    };

    for (const tracer of this.tracers) {
      const alpha = cfg.maxAlpha * envelope(tracer.age, peak, fade);
      if (alpha < 0.02) continue;
      const x = Math.round(tracer.x);
      const y = Math.round(tracer.y);
      ctx.fillStyle = `rgba(128, 128, 128, ${alpha})`;
      dot(x, y - 1);
      dot(x - 1, y);
      dot(x + 1, y);
      dot(x, y + 1);
      ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
      dot(x, y);
    }
  }

  private targetCount(): number {
    const cfg = this.config.particles;
    const area = this.bounds.width * this.bounds.height;
    return clamp(Math.round(area / Math.max(200, cfg.pxPerParticle)), cfg.minCount, cfg.maxCount);
  }

  private syncCount(stagger: boolean): void {
    const target = this.config.particles.enabled ? this.targetCount() : 0;
    while (this.tracers.length < target) {
      const tracer: Tracer = { x: 0, y: 0, age: 0, size: 1, shade: 0 };
      this.birth(tracer, stagger);
      this.tracers.push(tracer);
    }
    if (this.tracers.length > target) this.tracers.length = target;
  }

  private birth(tracer: Tracer, stagger: boolean): void {
    const cfg = this.config.particles;
    const t = clamp(cfg.sizeRandom, 0, 10) / 10;
    tracer.x = Math.random() * this.bounds.width;
    tracer.y = Math.random() * this.bounds.height;
    tracer.age = stagger ? Math.random() * cfg.life : 0;
    tracer.size = Math.max(0.5, cfg.size * (1 - t + Math.random() * t * 2));
    tracer.shade = Math.random();
  }
}

function envelope(age: number, peak: number, fade: number): number {
  if (age <= peak) return clamp(age / peak, 0, 1);
  return clamp(1 - (age - peak) / fade, 0, 1);
}
